import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Shield, Folder, MessageSquare, FileCheck, CreditCard } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_001924de-85a6-4169-ab14-49ef05e58c3d/artifacts/qt88pa3s_checkmate%20by%20memarketgroup_1.png";

export default function Landing() {
  const features = [
    {
      icon: <Folder className="w-6 h-6" />,
      title: "Project Management",
      description: "Organize all your projects in one secure place with real-time status tracking."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure File Sharing",
      description: "Upload and download files securely with enterprise-grade encryption."
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Direct Messaging",
      description: "Communicate directly with your project team in context."
    },
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "Contract Management",
      description: "Review and sign contracts digitally with full audit trails."
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Payment Tracking",
      description: "Track payment status and requirements for all your projects."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0B1120]">
      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-[#0B1120] to-[#050914]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234A90D9' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <nav className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Checkmate" className="h-20 md:h-24 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#4A90D9] hover:bg-[#3A80C9] text-white" data-testid="nav-register-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </nav>

        <div className="relative z-10 px-6 py-24 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-black tracking-tight text-white mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s' }}>
              Your Strategic
              <span className="block text-[#4A90D9]">Client Portal</span>
            </h1>
            <p className="text-xl text-slate-400 mb-8 leading-relaxed animate-fade-in-up opacity-0" style={{ animationDelay: '0.2s' }}>
              Streamline your business relationships with secure file sharing, project management, 
              and seamless communication—all in one powerful platform.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s' }}>
              <Link to="/register">
                <Button size="lg" className="bg-[#4A90D9] hover:bg-[#3A80C9] text-white px-8 btn-hover" data-testid="hero-get-started-btn">
                  Get Started <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-white/10 hover:text-white" data-testid="hero-login-btn">
                  Client Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Everything You Need
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            A complete solution for managing client relationships and project deliverables.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 rounded-xl bg-[#111827] border border-white/10 card-hover animate-fade-in-up opacity-0"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              <div className="w-12 h-12 rounded-lg bg-[#4A90D9]/20 flex items-center justify-center text-[#4A90D9] mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-slate-400">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="p-12 rounded-2xl glass border border-white/10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Join the platform trusted by Me Marketing Group LLC clients for secure project collaboration.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-[#4A90D9] hover:bg-[#3A80C9] text-white px-8 btn-hover" data-testid="cta-register-btn">
                Create Your Account <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Checkmate" className="h-12 w-auto" />
          </div>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Me Marketing Group LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
